import { SupplierBillingAddressModel } from '../models/SupplierBillingAddressModel';
import { useIntl } from 'react-intl';
import { FormField, GenericForm } from '../../../components/GenericForm';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Modal } from 'react-bootstrap';
import {
  createSupplierBillingAddress,
  listSupplierBillingAddresses,
  updateSupplierBillingAddress,
} from '../services/SupplierBillingAddressCRUD';
import COUNTRIES from '../../../constants/countries';
import COUNTRY_NAMES from '../../../constants/countryNames';

const LOADING_STATUS = 'LOADING_STATUS';
const IDLE_STATUS = 'IDLE_STATUS';

export default function SupplierAddresses({ id }) {
  const intl = useIntl();
  const [status, setStatus] = useState<string>(IDLE_STATUS);
  const [billingAddresses, setBillingAddresses] = useState<
    Array<SupplierBillingAddressModel>
  >([]);
  const [billingAddressErrors, setBillingAddressErrors] = useState<
    Array<object>
  >([]);
  const [billingAddressStatusses, setBillingAddressStatusses] = useState<
    Array<string>
  >([LOADING_STATUS]);
  const [visibleBillingAddressModal, setVisibleBillingAddressModal] =
    useState<boolean>(false);

  const billingAddressesFields: FormField<SupplierBillingAddressModel>[] = [
    {
      id: 'name',
      label: intl.formatMessage({ id: 'supplierDetail.nameLabel' }),
      inputType: 'text',
    },
    {
      id: 'vat_number',
      label: intl.formatMessage({ id: 'supplierDetail.vatNumberLabel' }),
      inputType: 'text',
    },
    {
      id: 'street',
      label: intl.formatMessage({ id: 'supplierDetail.streetLabel' }),
      inputType: 'text',
    },
    {
      id: 'postcode',
      label: intl.formatMessage({ id: 'supplierDetail.postcodeLabel' }),
      inputType: 'text',
    },
    {
      id: 'city',
      label: intl.formatMessage({ id: 'supplierDetail.cityLabel' }),
      inputType: 'text',
    },
    {
      id: 'country',
      label: intl.formatMessage({ id: 'supplierDetail.countryLabel' }),
      inputType: 'select',
      options: COUNTRIES.map((country) => ({
        value: country,
        label: COUNTRY_NAMES[country],
      })),
    },
  ];

  const initialBillingAddressFormState = {
    supplier: parseInt(id),
    name: '',
    street: '',
    postcode: '',
    city: '',
    region: '',
    country: 'ES',
    vat_number: '',
    state: 'active',
  } as SupplierBillingAddressModel;
  const [billingAddressFormState, setBillingAddressFormState] =
    useState<SupplierBillingAddressModel>(initialBillingAddressFormState);
  const [billingAddressFormError, setBillingAddressFormError] =
    useState<object>({});

  const fetchBillingAddresses = async (supplierId) => {
    try {
      setStatus(LOADING_STATUS);
      const { data } = await listSupplierBillingAddresses(supplierId);
      setBillingAddresses(data);
      setBillingAddressStatusses(data.map(() => IDLE_STATUS));
      setBillingAddressErrors(data.map(() => ({})));
    } catch (error) {
      console.warn(error);
    } finally {
      setStatus(IDLE_STATUS);
    }
  };

  useEffect(() => {
    fetchBillingAddresses(id);
  }, [id]);

  async function onSubmitBillingAddress(
    formState: SupplierBillingAddressModel
  ) {
    try {
      setStatus(LOADING_STATUS);
      if (formState.id) {
        await updateSupplierBillingAddress(formState);
      } else {
        await createSupplierBillingAddress(formState);
      }
      await fetchBillingAddresses(id);
      setVisibleBillingAddressModal(false);
      setBillingAddressFormState(initialBillingAddressFormState);
      toast.success(
        intl.formatMessage({ id: 'supplierDetail.billingAddressSaved' })
      );
    } catch (error) {
      console.warn(error);
      if (error.response?.data) {
        setBillingAddressFormError(error.response?.data);
      }
      toast.error(
        intl.formatMessage({ id: 'supplierDetail.errorSavingBillingAddress' })
      );
    } finally {
      setStatus(IDLE_STATUS);
    }
  }

  async function onUpdateBillingAddress(
    formState: SupplierBillingAddressModel,
    index: number
  ) {
    try {
      const newStatusses = [...billingAddressStatusses];
      newStatusses[index] = LOADING_STATUS;
      setBillingAddressStatusses(newStatusses);

      await updateSupplierBillingAddress(formState);
      await fetchBillingAddresses(id);

      toast.success(
        intl.formatMessage({ id: 'supplierDetail.billingAddressSaved' })
      );
    } catch (error) {
      console.warn(error);
      if (error.response?.data) {
        const newErrors = [...billingAddressErrors];
        newErrors[index] = error.response?.data;
        setBillingAddressErrors(newErrors);
      }
      toast.error(
        intl.formatMessage({ id: 'supplierDetail.errorSavingBillingAddress' })
      );
    } finally {
      const newStatusses = [...billingAddressStatusses];
      newStatusses[index] = IDLE_STATUS;
      setBillingAddressStatusses(newStatusses);
    }
  }

  function onAddBillingAddress() {
    setBillingAddressFormState(initialBillingAddressFormState);
    setVisibleBillingAddressModal(true);
  }

  return (
    <>
      <div className="card p-10">
        <div className="d-flex justify-content-between align-items-center mb-8">
          <h2>
            {intl.formatMessage({ id: 'supplierDetail.billingAddresses' })}
          </h2>
          {billingAddresses.length === 0 && (
            <button onClick={onAddBillingAddress} className="btn btn-primary">
              <span className="fa fa-plus me-2" />
              {intl.formatMessage({ id: 'supplierDetail.addBillingAddress' })}
            </button>
          )}
        </div>
        {status === LOADING_STATUS && billingAddresses.length === 0 && (
          <div className="spinner-border text-primary" role="status" />
        )}
        {billingAddresses.length === 0 &&
          status === IDLE_STATUS &&
          intl.formatMessage({ id: 'supplierDetail.noBillingAddresses' })}
        {billingAddresses.map((billingAddress, index) => (
          <GenericForm
            key={billingAddress.id}
            columns={2}
            error={billingAddressErrors[index]}
            editing={true}
            title={`${intl.formatMessage({
              id: 'supplierDetail.billingAddress',
            })} #${billingAddress.id}`}
            initialState={billingAddress}
            fields={billingAddressesFields}
            ctaLabel={intl.formatMessage({
              id: 'supplierDetail.saveChanges',
            })}
            onSubmit={(formState) => onUpdateBillingAddress(formState, index)}
            submitting={billingAddressStatusses[index] === LOADING_STATUS}
            submittingLabel={intl.formatMessage({
              id: 'supplierDetail.pleaseWait',
            })}
          />
        ))}
      </div>

      <Modal
        show={visibleBillingAddressModal}
        onHide={() => setVisibleBillingAddressModal(false)}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {intl.formatMessage({ id: 'supplierDetail.addBillingAddress' })}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => setVisibleBillingAddressModal(false)}
            ></button>
          </div>
          <div className="modal-body">
            <GenericForm
              columns={2}
              error={billingAddressFormError}
              editing={true}
              initialState={billingAddressFormState}
              fields={billingAddressesFields}
              ctaLabel={intl.formatMessage({
                id: 'supplierDetail.saveChanges',
              })}
              onSubmit={onSubmitBillingAddress}
              submitting={status === LOADING_STATUS}
              submittingLabel={intl.formatMessage({
                id: 'supplierDetail.pleaseWait',
              })}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
